import React, { useEffect, useRef, useState } from "react";
import { HiFilter, HiSearch } from "react-icons/hi";
import axios from "axios";
import TableLoader from "Components/Common/Loader/TableLoader";
import Pagination from "Components/Pagination/Pagination";
import IncomingCallSummary from "Screens/Admin/CallHistories/components/IncomingCallSummary";

const CallHistoryTable = () => {
  const [isDurationDropdownOpen, setIsDurationDropdownOpen] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState("All Durations");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const durationDropdownRef = useRef(null);
  const [callHistories, setCallHistories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("call-history");

  // Track if component has mounted to prevent duplicate API calls
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const toggleDurationDropdown = () =>
    setIsDurationDropdownOpen(!isDurationDropdownOpen);

  // Handle duration filter
  const handleDurationClick = (durationOption, durationValue) => {
    setSelectedDuration(durationOption);
    const newFilters = { ...filters, duration: durationValue };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 when filtering
    getCallHistories(1, newFilters);
    setIsDurationDropdownOpen(false);
  };

  // Handle date filter
  const handleDateChange = (type, value) => {
    let newFilters = { ...filters };

    if (type === "startDate") {
      setStartDate(value);
      if (value) {
        const start = new Date(value);
        start.setHours(0, 0, 0, 0);
        newFilters.startDate = start.toISOString();
      } else {
        delete newFilters.startDate;
      }
    } else if (type === "endDate") {
      setEndDate(value);
      if (value) {
        const end = new Date(value);
        end.setHours(23, 59, 59, 999);
        newFilters.endDate = end.toISOString();
      } else {
        delete newFilters.endDate;
      }
    }

    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 when filtering
    getCallHistories(1, newFilters);
  };

  // Close dropdown when clicking outside
  const handleClickOutside = (event) => {
    if (
      durationDropdownRef.current &&
      !durationDropdownRef.current.contains(event.target)
    ) {
      setIsDurationDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initial load effect - only runs once on mount
  useEffect(() => {
    if (!hasInitialLoad) {
      getCallHistories(1, filters);
      setHasInitialLoad(true);
    }
  }, []);

  // Pagination effect - only runs after initial load when currentPage changes
  useEffect(() => {
    if (!hasInitialLoad) return; // Skip if initial load hasn't happened
    getCallHistories(currentPage, filters);
  }, [currentPage]);

  const getCallHistories = async (page = 1, filters = {}) => {
    try {
      const params = new URLSearchParams({ page, limit: 5 });

      if (filters?.startDate) params.append("startDate", filters?.startDate);
      if (filters?.endDate) params.append("endDate", filters?.endDate);
      if (filters?.duration) params.append("duration", filters?.duration);
      setLoading(true);

      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/call/get-all-call-histories?${params.toString()}`
      );

      setCallHistories(response.data.data);
      // console.log("Call history data", response.data);
      setTotalPages(response.data.pagination.totalPages);
      setCurrentPage(response.data.pagination.currentPage);
      setLoading(false);
    } catch (error) {
      console.log("Error fetching call histories:", error);
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // The API call will be handled by the useEffect for currentPage change
  };

  return (
    <div className="py-5">
      <div className="mb-4 flex border-b">
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === "call-history"
              ? "border-b-2 border-[#c51369] text-[#c51369]"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("call-history")}
        >
          Call History
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === "call-summary"
              ? "border-b-2 border-[#c51369] text-[#c51369]"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("call-summary")}
        >
          Call Summary
        </button>
      </div>
      {activeTab === "call-history" ? (
        <div>
          <div className="flex flex-col items-center justify-start gap-2 space-y-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:space-y-0">
            {/* Duration Dropdown */}
            <div
              className="md:min-w-1/5 relative w-full md:w-1/5"
              ref={durationDropdownRef}
            >
              <button
                className="flex w-full items-center rounded-md border bg-white px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                onClick={toggleDurationDropdown}
              >
                <HiFilter color="#c51369" className="mr-2 h-5 w-5" />
                {selectedDuration}
              </button>
              {isDurationDropdownOpen && (
                <div className="absolute left-0 z-10 mt-2 w-full rounded-md border bg-white shadow-lg">
                  <ul className="py-2">
                    <li
                      className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                      onClick={() => handleDurationClick("All Durations", null)}
                    >
                      All Durations
                    </li>
                    <li
                      className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                      onClick={() => handleDurationClick("> 1 Minute", 1)}
                    >
                      {"> 1 Minute"}
                    </li>
                    <li
                      className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                      onClick={() => handleDurationClick("> 2 Minutes", 2)}
                    >
                      {"> 2 Minutes"}
                    </li>
                    <li
                      className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                      onClick={() => handleDurationClick("> 5 Minutes", 5)}
                    >
                      {"> 5 Minutes"}
                    </li>
                    <li
                      className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                      onClick={() => handleDurationClick("> 10 Minutes", 10)}
                    >
                      {"> 10 Minutes"}
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Start Date Input */}
            <div className="md:min-w-1/5 w-full md:w-1/5">
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange("startDate", e.target.value)}
                className="block w-full rounded-md border px-4 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
              />
            </div>

            {/* End Date Input */}
            <div className="md:min-w-1/5 w-full md:w-1/5">
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange("endDate", e.target.value)}
                className="block w-full rounded-md border px-4 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
              />
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <TableLoader />
          ) : (
            <div className="mx-auto mt-5 w-auto rounded-t-2xl">
              <div className="overflow-x-auto rounded-t-2xl">
                <table className="w-full rounded-t-2xl text-left text-sm">
                  <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                    <tr>
                      <th className="px-6 py-3">Sl No.</th>
                      <th className="px-6 py-3">Call Type</th>
                      <th className="px-6 py-3">Caller</th>
                      <th className="px-6 py-3">Receiver</th>
                      <th className="px-6 py-3">Date</th>
                      <th className="px-6 py-3">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callHistories.map((call, index) => (
                      <tr
                        key={index}
                        className="text-md bg-white tracking-wide text-gray-700"
                      >
                        <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                          {(currentPage - 1) * 5 + index + 1}
                        </td>
                        <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                          {call?.callType}
                        </td>
                        <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                          <div className="flex items-center">
                            <img
                              src={
                                call?.callerUserID?.selected_Avatar?.avatarUrl
                              }
                              alt="avatar"
                              className="h-10 w-10 rounded-full"
                            />
                            <span className="ml-4">
                              {call?.callerUserID?.fullName}
                            </span>
                          </div>
                          <span className="ml-4">
                            +{call?.callerUserID?.phone}
                          </span>
                        </td>
                        <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                          <div className="flex items-center">
                            <img
                              src={
                                call?.receiverUserID?.selected_Avatar?.avatarUrl
                              }
                              alt="avatar"
                              className="h-10 w-10 rounded-full"
                            />
                            <span className="ml-4">
                              {call?.receiverUserID?.fullName}
                            </span>
                          </div>
                          <span className="ml-4">
                            +{call?.receiverUserID?.phone}
                          </span>
                        </td>
                        <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                          {new Date(call?.startTime).toLocaleDateString()}
                        </td>
                        <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                          {Math.floor(call?.callDuration / 60)} mins
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      ) : (
        <IncomingCallSummary />
      )}
    </div>
  );
};

export default CallHistoryTable;
